/**
 * Escribir qué sitio se quiere: la caja de la descripción con lo que viaja con
 * ella, y debajo la ayuda.
 *
 * La comparten la pantalla de inicio y el primer paso del asistente, que eran
 * dos copias del mismo bloque. El orden es el de la tarea: primero se escribe;
 * el negocio y lo que se quiere (`BriefChips`) van DENTRO de la caja, junto a
 * «Generar», porque viajan con el texto, igual que las fotos propias (`photos`,
 * ver `wizardPhotos`); debajo, una sola línea de ayuda —lo que
 * falta para poder generar o, si no, lo que cuesta—. Las ideas de ejemplo van
 * bajo el título (`IdeaShuffle`).
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react'
import { IconArrowUp, IconLoader2, IconPhotoPlus, IconX } from '@tabler/icons-react'
import { useI18n } from '../../lib/i18n'
import type { WelcomeAnswers } from '../../lib/welcomeAnswers'
import type { WizardPhotos } from '../../lib/wizardPhotos'
import { BriefChips } from '../BriefChips'
import { MAX_PROMPT, MIN_PROMPT } from './promptConfig'

export type PromptIdea = { label: string; prompt: string }

/* La entrada de un ejemplo: cuánto tarda cada palabra, el máximo entre una y la
   siguiente y cuánto tardan todas juntas (más o menos), en milisegundos. */
const REVEAL_WORD = 560
const REVEAL_MAX_STAGGER = 45
const REVEAL_TOTAL = 900

export function PromptComposer({ value, onChange, onSubmit, brief, onBrief, photos, note, fieldRef, ariaLabel, revealKey = 0, placeholder, leading }: {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  brief: WelcomeAnswers | null
  onBrief: (answers: WelcomeAnswers) => void
  /** Las fotos propias para la IA. Sin ellas, la caja no ofrece subir. */
  photos?: WizardPhotos | undefined
  /** Lo que se dice bajo la caja cuando no falta nada: lo que cuesta generar. */
  note?: string | undefined
  fieldRef?: RefObject<HTMLTextAreaElement | null> | undefined
  ariaLabel?: string | undefined
  /** Otro texto de ayuda en el campo. La caja incrustada lo usa para llevar
   *  el título dentro, sin cabecera encima. */
  placeholder?: string | undefined
  /** Lo primero de la fila de abajo, dentro de la caja (p. ej. los ejemplos). */
  leading?: ReactNode
  /**
   * Sube cuando lo escrito llega de un ejemplo («Prueba un ejemplo»): la caja
   * lo hace aparecer palabra a palabra en vez de soltarlo de golpe.
   */
  revealKey?: number
}) {
  const { t } = useI18n()
  /* El campo crece con lo que se escribe desde unas tres líneas, hasta un tope
     (`max-height` en el CSS); a partir de ahí se desplaza por dentro. Con una
     altura fija quedaba un hueco blanco entre el texto y los controles. */
  const innerRef = useRef<HTMLTextAreaElement>(null)
  const ref = fieldRef ?? innerRef
  useLayoutEffect(() => {
    const field = ref.current
    if (!field) return
    field.style.height = 'auto'
    field.style.height = `${field.scrollHeight}px`
  }, [value, ref])
  /* Y al cambiar de ancho, que es cuando el mismo texto pasa a ocupar más (o
     menos) líneas: sin esto, al estrechar la ventana o girar el teléfono el
     campo se quedaba con la altura de antes y cortaba el texto. */
  useEffect(() => {
    const field = ref.current
    if (!field || typeof ResizeObserver === 'undefined') return
    let width = field.clientWidth
    const observer = new ResizeObserver(() => {
      if (field.clientWidth === width) return
      width = field.clientWidth
      field.style.height = 'auto'
      field.style.height = `${field.scrollHeight}px`
    })
    observer.observe(field)
    return () => observer.disconnect()
  }, [ref])
  /*
   * La entrada de un ejemplo: cada palabra se aclara desde un desenfoque y sube
   * un pelo, una detrás de otra, y el texto entero tarda lo mismo sea largo o
   * corto. Un `textarea` no deja animar sus palabras, así que mientras dura se
   * pinta encima una copia con la misma letra y el mismo relleno (`.cwa-prompt-
   * reveal`) y el campo queda transparente debajo; al acabar se va la copia y
   * queda el texto, editable. Escribir encima la corta en seco. Quien pide
   * menos movimiento lo ve aparecer sin más.
   */
  const valueRef = useRef(value)
  valueRef.current = value
  const [reveal, setReveal] = useState<{ key: number; words: string[]; stagger: number } | null>(null)
  useEffect(() => {
    if (!revealKey) return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
    const parts = valueRef.current.split(/(\s+)/)
    const count = parts.filter((part) => part.trim()).length
    const stagger = Math.min(REVEAL_MAX_STAGGER, REVEAL_TOTAL / Math.max(count, 1))
    setReveal({ key: revealKey, words: parts, stagger })
    const timer = window.setTimeout(() => setReveal(null), count * stagger + REVEAL_WORD)
    return () => window.clearTimeout(timer)
  }, [revealKey])
  const length = value.trim().length
  /* Mientras sube una foto no se genera: la IA no la vería. */
  const uploading = (photos?.pending.length ?? 0) > 0
  const ready = length >= MIN_PROMPT && !uploading
  /* Una sola línea: mientras se escribe, cuánto falta; cerca del tope, cuánto
     queda; si no, lo que cuesta. El contador no sale desde la primera letra:
     convertiría una ayuda en una cuenta atrás. Lo que pasa con las fotos va
     antes: es lo último que hizo. */
  const line = length > 0 && length < MIN_PROMPT
    ? t('cwa.minChars', { min: MIN_PROMPT, count: length, missing: MIN_PROMPT - length })
    : uploading
      ? t('brief.photosUploading')
      : photos?.problem === 'failed'
        ? t('brief.photosFailed')
        : photos?.problem === 'limit'
          ? t('brief.photosLimit', { max: photos.limit })
          : length > MAX_PROMPT - 100
            ? t('cwa.charsLeft', { count: MAX_PROMPT - length })
            : note ?? t('cwa.canChange')

  /*
   * Soltar fotos en CUALQUIER parte de la pantalla, no sólo sobre la caja: en
   * cuanto entra un archivo arrastrado en la ventana, la caja se pone en modo
   * «recibir» y lo que se suelte, donde sea, va a sus fotos. Sólo archivos: un
   * texto arrastrado sigue yendo al campo.
   *
   * Escucha la ventana, y la pantalla de inicio sigue montada debajo del
   * asistente con su propia caja: sólo responde la que se ve (la que está
   * encima en el centro de la tarjeta), o las fotos entrarían dos veces.
   */
  const cardRef = useRef<HTMLDivElement>(null)
  const [dropping, setDropping] = useState(false)
  const addRef = useRef(photos?.add)
  addRef.current = photos?.add
  const acceptsPhotos = Boolean(photos)
  useEffect(() => {
    if (!acceptsPhotos) return
    let depth = 0
    let active = false
    const carriesFiles = (event: DragEvent) => Array.from(event.dataTransfer?.types ?? []).includes('Files')
    const onTop = () => {
      const card = cardRef.current
      const box = card?.getBoundingClientRect()
      if (!card || !box?.width) return false
      const hit = document.elementFromPoint(box.left + box.width / 2, box.top + Math.min(box.height / 2, 48))
      return Boolean(hit && card.contains(hit))
    }
    const reset = () => { depth = 0; active = false; setDropping(false) }
    const enter = (event: DragEvent) => {
      if (!carriesFiles(event)) return
      depth += 1
      if (depth === 1) { active = onTop(); setDropping(active) }
    }
    const over = (event: DragEvent) => {
      if (!active || !carriesFiles(event)) return
      event.preventDefault()
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
    }
    const leave = (event: DragEvent) => {
      if (!carriesFiles(event)) return
      depth = Math.max(0, depth - 1)
      if (depth === 0) reset()
    }
    const drop = (event: DragEvent) => {
      const wasActive = active
      reset()
      if (!wasActive || !carriesFiles(event)) return
      /* Sin esto el navegador abre la imagen y se pierde la pantalla. */
      event.preventDefault()
      addRef.current?.(Array.from(event.dataTransfer?.files ?? []))
    }
    window.addEventListener('dragenter', enter)
    window.addEventListener('dragover', over)
    window.addEventListener('dragleave', leave)
    window.addEventListener('drop', drop)
    /* Arrastrar y soltar fuera de la ventana no avisa con `drop`. */
    window.addEventListener('dragend', reset)
    return () => {
      window.removeEventListener('dragenter', enter)
      window.removeEventListener('dragover', over)
      window.removeEventListener('dragleave', leave)
      window.removeEventListener('drop', drop)
      window.removeEventListener('dragend', reset)
    }
  }, [acceptsPhotos])

  return (
    <div className="cwa-composer">
      <div ref={cardRef} className={`cwa-prompt-card${dropping ? ' is-dropping' : ''}`}>
        {dropping ? (
          <div className="cwa-drop-hint" aria-hidden="true">
            <IconPhotoPlus size={20} />
            <span>{t('brief.photosDrop')}</span>
          </div>
        ) : null}
        <div className="cwa-prompt-field-wrap">
        <textarea
          ref={ref}
          className={`cwa-prompt-field${reveal ? ' is-revealing' : ''}`}
          value={value}
          maxLength={MAX_PROMPT}
          aria-label={ariaLabel}
          placeholder={placeholder ?? t('cwa.promptPlaceholder')}
          onChange={(event) => { setReveal(null); onChange(event.target.value) }}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && ready) onSubmit()
          }}
        />
        {reveal ? (
          <div key={reveal.key} className="cwa-prompt-field cwa-prompt-reveal" aria-hidden="true">
            {(() => {
              let index = 0
              return reveal.words.map((part, at) => (part.trim()
                ? <span key={at} className="cwa-prompt-reveal-word" style={{ '--i': index++, '--stagger': `${reveal.stagger}ms` } as CSSProperties}>{part}</span>
                : part))
            })()}
          </div>
        ) : null}
        </div>
        {photos && (photos.photos.length || photos.pending.length) ? <PhotoStrip photos={photos} /> : null}
        <div className="cwa-prompt-foot is-inside has-brief">
          <div className="cwa-prompt-extras">
            {leading}
            {photos ? <PhotoButton photos={photos} /> : null}
            <BriefChips answers={brief} onChange={onBrief} />
          </div>
          {/* «Generar» como en un chat: redondo, con su flecha, en la esquina de
              la caja. Gris hasta que hay texto suficiente. */}
          <button className="cwa-send" type="button" disabled={!ready} aria-label={t('cwa.generate')} title={`${t('cwa.generate')} · ${t('cwa.generateShortcut')}`} onClick={onSubmit}>
            <IconArrowUp size={17} stroke={2.4} aria-hidden="true" />
          </button>
        </div>
      </div>
      {/* `aria-live`: cambia sola al escribir, y quien usa lector de pantalla
          tiene que enterarse de cuándo ya puede generar. */}
      <p className="cwa-composer-note" aria-live="polite">{line}</p>
    </div>
  )
}

/**
 * «Fotos»: el primero del pie, porque es lo que más cambia el sitio. Un
 * `<input>` de archivos invisible encima, como los `<select>` de las fichas: en
 * el teléfono abre la fototeca del sistema.
 */
function PhotoButton({ photos }: { photos: WizardPhotos }) {
  const { t } = useI18n()
  const count = photos.photos.length + photos.pending.length
  const full = count >= photos.limit
  return (
    <label className={`cwa-brief-btn cwa-photos-btn${count ? '' : ' is-add'}${full ? ' is-full' : ''}`} title={t('brief.photosHint')}>
      <IconPhotoPlus size={14} aria-hidden="true" />
      <span className="cwa-brief-btn-value">{count ? t('brief.photosCount', { count, max: photos.limit }) : t('brief.photos')}</span>
      <input
        type="file"
        accept="image/*"
        multiple
        disabled={full}
        aria-label={t('brief.photosAdd')}
        onChange={(event) => {
          photos.add(Array.from(event.target.files ?? []))
          /* Vaciado: elegir otra vez la misma foto tiene que volver a avisar. */
          event.target.value = ''
        }}
      />
    </label>
  )
}

/** Las elegidas, en miniatura y en su orden: las primeras van a la portada. */
function PhotoStrip({ photos }: { photos: WizardPhotos }) {
  const { t } = useI18n()
  return (
    <ul className="cwa-photos" aria-label={t('brief.photos')}>
      {photos.photos.map((photo, index) => (
        <li key={photo.id} className="cwa-photo">
          <img src={photo.url} alt={t('brief.photoAlt', { n: index + 1 })} loading="lazy" />
          <button type="button" className="cwa-photo-remove" aria-label={t('brief.photosRemove', { n: index + 1 })} onClick={() => photos.remove(photo.id)}>
            <IconX size={11} aria-hidden="true" />
          </button>
        </li>
      ))}
      {photos.pending.map((photo) => (
        <li key={photo.key} className="cwa-photo is-pending" aria-busy="true">
          <img src={photo.preview} alt="" />
          <span className="cwa-photo-busy"><IconLoader2 className="dash-spin" size={15} aria-hidden="true" /></span>
        </li>
      ))}
    </ul>
  )
}
